import { useCallback, useEffect, useMemo, useState } from 'react';
import { tradingHistoryService } from '../services/tradingHistoryService';
import { TradingSession } from '../types';

interface UseTradingHistoryProps {
  apiBaseUrl: string;
  viewerUserKey?: number;
  targetUserKey?: number;
}

export const useTradingHistory = ({
  apiBaseUrl,
  viewerUserKey,
  targetUserKey,
}: UseTradingHistoryProps) => {
  const [sessions, setSessions] = useState<TradingSession[]>([]);
  const [currentSession, setCurrentSession] = useState<TradingSession | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // 20개로 변경
  const [totalItems, setTotalItems] = useState(0);
  const [isLimited, setIsLimited] = useState(false);
  const [maxAllowed, setMaxAllowed] = useState(0);

  // 사용자의 모든 거래 세션 조회
  const loadTradingSessions = useCallback(
    async (page = 1, forceRefresh = false) => {
      if (!viewerUserKey) return;

      setIsLoading(true);
      setError(null);

      try {
        const {
          sessions,
          total,
          isLimited: limited,
          maxAllowed: allowed,
        } = await tradingHistoryService.getTradingSessions(
          apiBaseUrl,
          viewerUserKey,
          targetUserKey,
          page,
          itemsPerPage,
          forceRefresh
        );
        // 빈 배열은 정상적인 경우이므로 오류로 처리하지 않음
        setSessions(sessions);
        setTotalItems(total);
        setIsLimited(limited || false);
        setMaxAllowed(allowed || total);
        setCurrentPage(page);
        setError(null);
      } catch (err) {
        // 실제 오류인 경우에만 오류 메시지 설정
        setError(
          err instanceof Error
            ? err.message
            : '거래 내역을 불러오는데 실패했습니다.'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, viewerUserKey, targetUserKey, itemsPerPage]
  );

  // 페이지 변경 핸들러
  const changePage = useCallback(
    (page: number) => {
      loadTradingSessions(page);
    },
    [loadTradingSessions]
  );

  // 특정 세션 상세 조회 (기존 세션 데이터를 사용하고 차트만 로드)
  const loadSessionDetail = useCallback(
    async (sessionId: string, existingSession?: TradingSession) => {
      setIsLoading(true);
      setError(null);

      try {
        let session: TradingSession;

        // 기존 세션 데이터가 있고 차트 데이터가 있으면 전체 세션을 다시 로드하되 차트는 유지
        if (
          existingSession &&
          existingSession.chartHistory &&
          existingSession.chartHistory.length > 0
        ) {
          console.log('📋 기존 세션 데이터 사용 (차트 데이터 유지):', {
            sessionId: existingSession.id,
            tradesCount: existingSession.trades?.length || 0,
            hasChartData: existingSession.chartHistory.length > 0,
          });

          // 전체 세션을 다시 로드 (매매일지 업데이트 반영)
          const updatedSession = await tradingHistoryService.getSessionDetail(
            apiBaseUrl,
            sessionId
          );

          // 기존 차트 데이터 유지
          session = {
            ...updatedSession,
            chartHistory: existingSession.chartHistory,
          };
        } else if (
          existingSession &&
          existingSession.trades &&
          existingSession.trades.length > 0
        ) {
          console.log('📋 기존 세션 데이터 사용 (차트 데이터 로드):', {
            sessionId: existingSession.id,
            tradesCount: existingSession.trades.length,
            hasChartData: existingSession.chartHistory?.length > 0,
          });

          // 차트 데이터만 로드
          session = await tradingHistoryService.loadChartDataForSession(
            apiBaseUrl,
            existingSession
          );
        } else {
          // 기존 데이터가 없으면 전체 API 호출
          session = await tradingHistoryService.getSessionDetail(
            apiBaseUrl,
            sessionId
          );
        }

        setCurrentSession(session);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : '세션 상세 정보를 불러오는데 실패했습니다.'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl]
  );

  // 매매노트 저장
  const saveTradingNote = useCallback(
    async (tradeId: string, noteData: any) => {
      try {
        await tradingHistoryService.saveTradingNote(
          apiBaseUrl,
          tradeId,
          noteData
        );
        // 현재 세션 다시 로드 (기존 세션 데이터를 전달하여 차트 데이터 재로드 방지)
        if (currentSession) {
          // 기존 세션 데이터를 전달하면 차트 데이터는 재로드하지 않고 세션 정보만 업데이트
          await loadSessionDetail(currentSession.id, currentSession);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : '매매노트 저장에 실패했습니다.'
        );
      }
    },
    [apiBaseUrl, currentSession, loadSessionDetail]
  );

  useEffect(() => {
    if (!viewerUserKey) {
      console.log('useTradingHistory: viewerUserKey가 없어서 로드 안 함');
      // 로그아웃 시 상태 초기화
      setSessions([]);
      setCurrentSession(null);
      setIsLoading(false);
      setError(null);
      setCurrentPage(1);
      setTotalItems(0);
      setIsLimited(false);
      setMaxAllowed(0);
      return;
    }

    console.log('useTradingHistory: 데이터 로드 시작');
    // 초기 로드 (1페이지)
    loadTradingSessions(1);
  }, [viewerUserKey, targetUserKey, loadTradingSessions]);

  const totalPages = useMemo(() => {
    if (itemsPerPage <= 0) return 1;
    return Math.max(1, Math.ceil(totalItems / itemsPerPage));
  }, [totalItems, itemsPerPage]);

  return {
    sessions,
    currentSession,
    isLoading,
    error,
    loadTradingSessions,
    loadSessionDetail,
    saveTradingNote,
    currentPage,
    totalPages,
    changePage,
    isLimited,
    maxAllowed,
  };
};
